import React from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '@smartphone-tycoon/shared-ui';
import { AlertTriangle, Users, Building } from 'lucide-react';

interface EmployeeAvailabilityWarningProps {
  isOpen: boolean;
  onClose: () => void;
  onProceedAnyway?: () => void;
  missingEmployees: Record<string, number>;
  techScore: number;
  currentYear: number;
}

export function EmployeeAvailabilityWarning({
  isOpen,
  onClose,
  onProceedAnyway,
  missingEmployees,
  techScore,
  currentYear
}: EmployeeAvailabilityWarningProps) {
  const roleNames: Record<string, string> = {
    designer: 'Designers',
    engineer: 'Engineers', 
    tester: 'Testers',
    assemblyWorker: 'Assembly Workers'
  };

  const totalMissing = Object.values(missingEmployees).reduce((sum, count) => sum + count, 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Insufficient Employees" size="md">
      <div className="space-y-4">
        {/* Warning icon and message */}
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-8 w-8 text-theme-warning" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-theme-text-primary">
              Not Enough Employees for Product Development
            </h3>
            <p className="mt-1 text-sm text-theme-text-secondary">
              You don't have enough employees to start developing this product.
              Higher tech products (Tech Score: {techScore}) require more employees,
              especially in {currentYear}.
            </p>
          </div>
        </div>

        {/* Missing employees breakdown */}
        <div className="bg-theme-error/10 border border-theme-error/20 rounded-lg p-4">
          <h4 className="flex items-center font-medium text-theme-error mb-2">
            <Users className="h-4 w-4 mr-2" />
            Missing Employees ({totalMissing} total)
          </h4>
          <div className="space-y-1">
            {Object.entries(missingEmployees).map(([role, count]) => (
              count > 0 && (
                <div key={role} className="text-sm text-theme-error">
                  • {count} more {roleNames[role] || role}
                </div>
              )
            ))}
          </div>
        </div>

        {/* Suggestions */}
        <div className="bg-theme-info/10 border border-theme-info/20 rounded-lg p-4">
          <h4 className="font-medium text-theme-info mb-2">Suggestions</h4>
          <ul className="space-y-2 text-sm text-theme-info">
            <li className="flex items-start">
              <Building className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>Build more offices, labs, or factories to increase employee capacity</span>
            </li>
            <li className="flex items-start">
              <Users className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>Hire the required employees from the Operations tab</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">💾</span>
              <span>Save your design and come back when you have enough employees</span>
            </li>
          </ul>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="secondary" onClick={onClose}>
            Understood
          </Button>
          {onProceedAnyway && (
            <Button
              variant="warning"
              onClick={onProceedAnyway}
              className="bg-theme-warning hover:bg-theme-warning/80 text-white"
            >
              Open Designer Anyway
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}